import type { FactFind } from '../../../shared/src/calculation/factFindTypes.ts';
import { annuityPreferenceOf, allowsResidual, allowsGuaranteed } from '../../../shared/src/calculation/annuityPreference.ts';
import type { DraftFactFind } from '../constants/initialFactFind.ts';
import { calculateAge } from '../utils/index.ts';
import {
    getRemainingGiftingCapacity,
    decidableClientLiabilities,
    buildDownsizeDetailsFromScenario,
    isDownsizerEligible,
    type FundingSource,
} from './supportAtHomeStrategyFunding.ts';
import { canCommenceAbpFromDownsizer } from '../../../shared/src/calculation/downsizerContributionUtils.ts';
import { getRecommendedEmergencyFund } from '../utils/emergencyFund.ts';
import { ZERO_INTEREST_DEBT_FLOOR } from './debtsToTest.ts';
import {
    getDownsizeNewHomePurchasePrices,
    resolveDownsizeSaleEconomics,
    type DownsizePropertySettings,
} from './supportAtHomeDownsizeScenarioUtils.ts';
import {
    collectLiquidAssetsForSahSuggestions,
    generateEntityIpSaleStrategies,
    type SahAvailableAsset,
} from '../utils/supportAtHomeSuggestionEngineFunding.ts';
import { FUNERAL_BOND_LIMIT } from '../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import type { StrategyCandidate } from './SupportAtHomeStrategyApplicator.ts';
import type { ProblemStatement } from './SupportAtHomeProblemIdentifier.ts';

import { REVERSE_MORTGAGE_DEFAULT_RATE } from '../../../shared/src/calculation/support/capitalisingLoans.ts';
import { annuitySizesFor } from './annuitySizes.ts';
// Gifting limit (Centrelink rules)
const GIFTING_5_YEAR_LIMIT = 30000; // $30,000 over 5 years

// Funeral bond exempt limit
/*
 * ONE FUNERAL BOND LIMIT, FROM THE RATES FILE.
 *
 * This was a local `const FUNERAL_BOND_EXEMPT_LIMIT = 15000`, shadowing the published figure of
 * $15,750 that the assessment itself uses. So the engine sized its funeral bond candidates $750 per
 * person short of the exemption, and the screen told the client a limit the assessment disagreed
 * with. A rate that lives in two places will eventually differ in two places — this one already did.
 */
const FUNERAL_BOND_EXEMPT_LIMIT = FUNERAL_BOND_LIMIT;

export interface AssetSourceWithFunding extends SahAvailableAsset {
    amountToTake?: number;
    originalValue?: number;
    fundingSources: FundingSource[];
    totalAmount?: number;
}

export interface PropertyStrategySettings extends DownsizePropertySettings {
    downsize?: boolean;
    increaseHomeEquity?: boolean;
    renovate?: boolean;
    upgrade?: boolean;
    reverseMortgage?: boolean;
    heas?: boolean;
}

export interface SupportAtHomeStrategySettingsInput {
    includeGifting?: boolean;
    includeFuneralBonds?: boolean;
    includeAnnuities?: boolean;
    includePropertyStrategies?: boolean;
    includeDebtRepayment?: boolean;
    propertyStrategies?: PropertyStrategySettings;
    /** The money no plan spends (the Support at home questions, 19 Oct 2026). Default: getRecommendedEmergencyFund. */
    emergencyReserve?: number;
    [key: string]: unknown;
}

/**
 * Generate gifting strategies with intelligent filtering
 */
export function generateGiftingStrategies(factFind: DraftFactFind, problemStatement: ProblemStatement, reserve?: number): StrategyCandidate[] {
    const strategies: StrategyCandidate[] = [];

    // Check if gifting is relevant
    if (!problemStatement.strategyRelevance.gifting.relevant) {
        return strategies; // Return empty array - strategy is not relevant
    }

    // Get available liquid assets (bank accounts, ABPs, shares)
    const availableAssets = getSahAvailableAssets(factFind, reserve);
    if (availableAssets.length === 0) {
        return strategies; // No assets to gift
    }

    // Test different gifting amounts
    // If full pension but NWOP Category B, test smaller amounts
    // If part/self-funded, test larger amounts to maximize pension gain
    const pensionStatus = problemStatement.pensionOptimization.status;

    let amountsToTest: number[] = [];
    if (pensionStatus === 'full') {
        // If full pension, still test small amounts in case it helps with contribution rates
        amountsToTest = [5000, 10000]; // Test smaller amounts
    } else if (pensionStatus === 'part') {
        // Part pension - test incremental amounts
        amountsToTest = [5000, 10000, 15000, 20000, 30000]; // Within 5-year limit
    } else {
        // Self-funded - test larger amounts to maximize pension qualification
        amountsToTest = [10000, 20000, 30000]; // Test up to 5-year limit
    }

    const remainingGiftingCapacity = getRemainingGiftingCapacity(factFind);

    // Filter amounts based on available assets and Centrelink gifting capacity (profile + limits)
    const totalAvailable = availableAssets.reduce((sum, asset) => sum + asset.available, 0);
    const maxGiftingAvailable = Math.max(0, totalAvailable - (reserve ?? getRecommendedEmergencyFund(factFind)));

    amountsToTest = amountsToTest.filter(
        (amount) =>
            amount <= maxGiftingAvailable &&
            amount <= GIFTING_5_YEAR_LIMIT &&
            amount <= remainingGiftingCapacity
    );

    // Generate strategies for each amount
    amountsToTest.forEach(amount => {
        // Find optimal asset source (prefer bank accounts, then ABPs, then shares)
        const assetSource = findOptimalAssetSource(availableAssets, amount);

        if (assetSource) {
            strategies.push({
                type: 'gifting',
                amount,
                assetType: assetSource.type,
                assetId: assetSource.id,
                assetDescription: assetSource.description,
                fundingSources: assetSource.fundingSources,
                name: `Gift $${amount.toLocaleString()} to Family`,
                description: `Gift $${amount.toLocaleString()} within Centrelink gifting limits to reduce assessable assets`
            });
        }
    });

    return strategies;
}

/**
 * Generate funeral bond strategies with intelligent filtering
 */
export function generateFuneralBondStrategies(factFind: DraftFactFind, problemStatement: ProblemStatement, reserve?: number): StrategyCandidate[] {
    const strategies: StrategyCandidate[] = [];

    // Check if funeral bonds are relevant
    if (!problemStatement.strategyRelevance.funeralBonds.relevant) {
        return strategies; // Return empty array - strategy is not relevant
    }

    // Get available liquid assets
    const availableAssets = getSahAvailableAssets(factFind, reserve);
    if (availableAssets.length === 0) {
        return strategies;
    }

    // Calculate current funeral bonds to determine remaining exempt capacity
    const currentFuneralBonds = calculateCurrentFuneralBonds(factFind);
    const isPartnered = factFind?.adviceFor === 'couple';
    const exemptLimit = isPartnered ? FUNERAL_BOND_EXEMPT_LIMIT * 2 : FUNERAL_BOND_EXEMPT_LIMIT;
    const remainingExempt = Math.max(0, exemptLimit - currentFuneralBonds);

    if (remainingExempt <= 0) {
        return strategies; // Already at exempt limit
    }

    /*
     * Purchase amounts tried, per person. The top of the list is the EXEMPTION itself, not a
     * round number near it: hard-coded at 15000 the engine stopped $750 per person short of
     * the limit it was trying to fill, so it could never propose the amount that uses the
     * strategy fully.
     */
    const amountsToTest = [5000, 10000, FUNERAL_BOND_EXEMPT_LIMIT];

    // Filter amounts based on available exempt capacity and assets
    const totalAvailable = availableAssets.reduce((sum, asset) => sum + asset.available, 0);
    const emergencyFundReserve = 50000;
    const maxAvailable = Math.max(0, totalAvailable - emergencyFundReserve);

    amountsToTest.forEach(amount => {
        // Calculate total amount (for couples, can do per person or combined)
        let totalAmount = amount;
        if (isPartnered) {
            // For couples, could do $15K per person = $30K total
            const perPersonAmount = Math.min(amount, remainingExempt / 2);
            totalAmount = perPersonAmount * 2;
        } else {
            totalAmount = Math.min(amount, remainingExempt);
        }

        // Cap at available funds
        totalAmount = Math.min(totalAmount, maxAvailable);
        totalAmount = Math.min(totalAmount, remainingExempt);

        if (totalAmount >= 5000) { // Minimum $5K to be worthwhile
            const assetSource = findOptimalAssetSource(availableAssets, totalAmount);

            if (assetSource) {
                strategies.push({
                    type: 'funeralBonds',
                    amount: totalAmount,
                    perPersonAmount: isPartnered ? totalAmount / 2 : totalAmount,
                    assetType: assetSource.type,
                    assetId: assetSource.id,
                    assetDescription: assetSource.description,
                    fundingSources: assetSource.fundingSources,
                    name: `Purchase $${totalAmount.toLocaleString()} Funeral Bonds`,
                    description: `Purchase prepaid funeral bonds (exempt asset) to reduce assessable assets by $${totalAmount.toLocaleString()}`
                });
            }
        }
    });

    return strategies;
}

/**
 * Generate annuity strategies (wrapper around existing AnnuityStrategies.js logic)
 */
export function generateAnnuityStrategies(factFind: DraftFactFind, _problemStatement: ProblemStatement, reserve?: number): StrategyCandidate[] {
    const strategies: StrategyCandidate[] = [];

    // Annuities are always relevant - they can help with pension, cashflow, and legacy
    // We'll generate a range of annuity strategies
    // The actual annuity calculations will be done using buildAnnuityImpact() from AnnuityStrategies.js

    const availableAssets = getSahAvailableAssets(factFind, reserve);
    if (availableAssets.length === 0) {
        return strategies;
    }

    const maxAvailable = sahAnnuityInvestable(factFind, reserve).amount;

    /*
     * HOW MUCH (owner, 21 Oct 2026 — the village review's decision 11, every module; annuitySizes.ts): up to a
     * quarter of what is available, and — where it would lift the Age Pension — the amount that lifts it most,
     * as a plan of its own. Previously a grid from $50,000 to $500,000, whatever the client held.
     */
    const sizes = annuitySizesFor(factFind, { investable: maxAvailable, capPct: 25, homeowner: Number(factFind.home?.value) > 0, lessCounted: maxAvailable });
    const amountsToTest = sizes.map((size) => ({ amount: size.pensionMax ? size.amount : Math.floor(size.amount / 10_000) * 10_000, pensionMax: size.pensionMax }))
        .filter(({ amount }) => amount >= 50_000 && amount <= maxAvailable);

    /*
     * RESIDUAL CAPITAL IS A CHOICE, AND IT WAS NEVER OFFERED.
     *
     * The comment here used to say the annuity's type, RCV and indexation "will be optimised in the
     * evaluator". They were not: the evaluator hard-coded a lifetime annuity with nil residual, so
     * that was the only annuity this engine could ever propose.
     *
     * It matters because Centrelink assesses a lifetime annuity at 60% of its purchase price
     * whichever residual it carries — provided the death benefit stays inside the capital access
     * schedule (annuityStrategies.ts) — while net worth values one at that scheduled death benefit
     * (a nil-RCV annuity really does leave nothing behind). So every annuity candidate lost the
     * legacy ranking by its full purchase price on day one. It was not being weighed against the
     * alternatives; it was being disqualified before the comparison started.
     *
     * Both ends are now offered so the ranking decides.
     *
     * THE MONEY-BACK TYPES FROM THE $50,000 MINIMUM (owner, 21 Oct 2026 — the guaranteed income review).
     * They used to start at $100,000, when income only was built for everyone and the money-back types were
     * the extra candidates. Income only is now the opt-in one (annuityPreference.ts), so with the floor at
     * $100,000 a household whose quarter is under that would get no guaranteed-income plan at all. The
     * types that return the money now start where any guaranteed income starts: the provider minimum.
     */
    const RCV_VARIANT_FLOOR = 50_000;
    // The client's own answer decides whether an annuity is offered at all, and which versions.
    const annuityPreference = annuityPreferenceOf(factFind as never);
    amountsToTest.forEach(({ amount, pensionMax }) => {
        const sizeName = pensionMax ? ', the amount that lifts the Age Pension most' : '';
        const assetSource = findOptimalAssetSource(availableAssets, amount);
        if (!assetSource) return;

        const common = {
            type: 'annuity' as const,
            purchaseAmount: amount,
            assetType: assetSource.type,
            assetId: assetSource.id,
            fundingSources: assetSource.fundingSources,
        };

        if (allowsResidual(annuityPreference, 0)) strategies.push({
            ...common,
            residualCapital: 0,
            name: `Purchase $${amount.toLocaleString()} Annuity (nil residual${sizeName})`,
            description: 'Purchase a lifetime annuity to reduce assessable assets and provide guaranteed income. This version pays the most income and leaves nothing of the purchase price to the estate.',
        });

        if (amount >= RCV_VARIANT_FLOOR && allowsResidual(annuityPreference, 1)) {
            strategies.push({
                ...common,
                residualCapital: 1,
                name: `Purchase $${amount.toLocaleString()} Annuity (capital returned${sizeName})`,
                description: 'The same annuity, but with the purchase price coming back to the estate on an early death — in full within the first half of life expectancy, less after that — rather than kept by the provider. It pays less income, and Centrelink still counts only 60% either way.',
            });
        }

        // The guaranteed shape: the full amount to the estate whenever death comes, at the least
        // income and with far less assets-test relief. Support at Home IS government-subsidised
        // aged care, so its clients qualify for it.
        if (amount >= RCV_VARIANT_FLOOR && allowsGuaranteed(annuityPreference, factFind as never)) {
            strategies.push({
                ...common,
                residualCapital: 1,
                annuityType: 'guaranteed',
                name: `Purchase $${amount.toLocaleString()} Annuity (full amount guaranteed${sizeName})`,
                description: 'A lifetime annuity and a life policy bought together, so the full purchase price comes back to the estate whenever death comes. It pays the least income of the three, and only the annuity half earns the 60% assets-test treatment; the policy is counted at what it cost.',
            });
        }
    });

    return strategies;
}

/**
 * Generate property strategies
 */
export function generatePropertyStrategies(factFind: DraftFactFind, _problemStatement: ProblemStatement, propertySettings: PropertyStrategySettings = {}, reserve?: number): StrategyCandidate[] {
    const strategies: StrategyCandidate[] = [];

    const currentHomeValue = factFind.home?.value || 0;
    if (currentHomeValue <= 0) {
        return strategies; // No home to work with
    }

    const increaseHomeEquityEnabled = propertySettings.increaseHomeEquity
        || propertySettings.renovate
        || propertySettings.upgrade;

    // Increase exempt home equity: renovate in place or buy up — same modelling (liquid assets → home value 1:1)
    if (increaseHomeEquityEnabled) {
        const availableAssets = getSahAvailableAssets(factFind, reserve);
        const totalAvailable = availableAssets.reduce((sum, asset) => sum + asset.available, 0);
        const emergencyFundReserve = 50000;
        const maxAvailable = Math.max(0, totalAvailable - emergencyFundReserve);
        const minLiquidityRequired = emergencyFundReserve + 20000;
        const maxEquityAmount = Math.max(0, totalAvailable - minLiquidityRequired);

        const equityAmountCandidates = new Set<number>();
        [20000, 50000, 100000, 150000, 200000].forEach((a) => {
            if (a <= maxAvailable && a <= maxEquityAmount) equityAmountCandidates.add(a);
        });
        [1.1, 1.2, 1.3, 1.5].forEach((ratio) => {
            const amount = Math.round(currentHomeValue * (ratio - 1));
            if (amount > 0 && amount <= maxAvailable && amount <= maxEquityAmount) {
                equityAmountCandidates.add(amount);
            }
        });

        [...equityAmountCandidates].sort((a, b) => a - b).forEach((amount) => {
            const assetSource = findOptimalAssetSource(availableAssets, amount);
            if (!assetSource) return;

            const remainingLiquidity = totalAvailable - amount;
            if (remainingLiquidity < minLiquidityRequired) return;

            const newHomeValue = currentHomeValue + amount;
            const strategyConfig: StrategyCandidate = {
                type: 'increaseHomeEquity',
                equityAmount: amount,
                renovationAmount: amount,
                currentHomeValue,
                newHomeValue,
                assetType: assetSource.type,
                assetId: assetSource.id,
                name: `Increase home equity ($${amount.toLocaleString()})`,
                description: `Convert $${amount.toLocaleString()} of liquid assets into exempt home equity (renovate in place or buy a more expensive home — modelled as $1 for $1 value uplift)`,
            };

            if (assetSource.fundingSources && Array.isArray(assetSource.fundingSources)) {
                strategyConfig.fundingSources = assetSource.fundingSources;
            }

            strategies.push(strategyConfig);
        });
    }

    // Downsize: sell home, purchase smaller property, allocate proceeds (bank / downsizer / debt)
    if (propertySettings.downsize) {
        const liabilities = decidableClientLiabilities(factFind);
        // canCommenceAbpFromDownsizer is the real (strict) calc-engine function; factFind's money
        // fields being read here (client/partner dob) are plain strings either way, so the cast is safe.
        const downsizerEligible = isDownsizerEligible(factFind);
        const { salePrice, sellingCosts } = resolveDownsizeSaleEconomics(currentHomeValue, propertySettings);
        const purchasePrices = getDownsizeNewHomePurchasePrices(currentHomeValue, propertySettings);

        purchasePrices.forEach((newHomePurchasePrice) => {
            const netProceeds = Math.max(0, salePrice - sellingCosts - newHomePurchasePrice);
            if (netProceeds <= 0) return;

            const baseScenario = { salePrice, sellingCosts, newHomePurchasePrice };

            const surplusDetails = buildDownsizeDetailsFromScenario(factFind, { ...baseScenario, useDownsizer: false });
            strategies.push({
                type: 'downsize',
                downsizeDetails: surplusDetails,
                currentHomeValue,
                newHomeValue: newHomePurchasePrice,
                name: `Downsize to $${newHomePurchasePrice.toLocaleString()} (surplus to cash)`,
                description: `Sell home and purchase smaller property; surplus $${netProceeds.toLocaleString()} to liquid assets`,
            });

            if (downsizerEligible) {
                const downsizerDetails = buildDownsizeDetailsFromScenario(factFind, { ...baseScenario, useDownsizer: true });
                strategies.push({
                    type: 'downsize',
                    downsizeDetails: downsizerDetails,
                    currentHomeValue,
                    newHomeValue: newHomePurchasePrice,
                    name: `Downsize to $${newHomePurchasePrice.toLocaleString()} (with downsizer)`,
                    description: `Downsize and contribute eligible proceeds to super via downsizer rules`,
                });

                if (canCommenceAbpFromDownsizer(factFind as unknown as FactFind, 'client')
                    || (factFind?.adviceFor === 'couple' && canCommenceAbpFromDownsizer(factFind as unknown as FactFind, 'partner'))) {
                    const abpDetails = buildDownsizeDetailsFromScenario(factFind, {
                        ...baseScenario,
                        useDownsizer: true,
                        commenceIncomeStream: true,
                    });
                    strategies.push({
                        type: 'downsize',
                        downsizeDetails: abpDetails,
                        currentHomeValue,
                        newHomeValue: newHomePurchasePrice,
                        name: `Downsize to $${newHomePurchasePrice.toLocaleString()} (downsizer to ABP)`,
                        description: `Downsize, contribute via downsizer rules, and commence account-based pension income where age-eligible (60+)`,
                    });
                }
            }

            liabilities.forEach((liability) => {
                const repayAmount = Math.min(liability.balance, netProceeds);
                if (repayAmount < 1000) return;
                const debtDetails = buildDownsizeDetailsFromScenario(factFind, {
                    ...baseScenario,
                    useDownsizer: false,
                    repayDebt: true,
                    selectedDebtKey: liability.key,
                    debtRepayment: repayAmount,
                });
                strategies.push({
                    type: 'downsize',
                    downsizeDetails: debtDetails,
                    currentHomeValue,
                    newHomeValue: newHomePurchasePrice,
                    name: `Downsize to $${newHomePurchasePrice.toLocaleString()} (repay ${liability.description})`,
                    description: `Downsize and apply $${repayAmount.toLocaleString()} of proceeds to ${liability.description}`,
                });
            });
        });
    }

    // Reverse mortgage: lump-sum equity release at conservative LVR steps
    if (propertySettings.reverseMortgage) {
        const clientAge = calculateAge(factFind.client?.dob) || 65;
        const partnerAge = factFind?.adviceFor === 'couple' ? calculateAge(factFind.partner?.dob) : null;
        const youngestAge = partnerAge ? Math.min(clientAge, partnerAge) : clientAge;
        const getMaxLVR = (age: number) => {
            if (age >= 80) return 50;
            if (age >= 75) return 45;
            if (age >= 70) return 40;
            if (age >= 65) return 30;
            if (age >= 60) return 20;
            return 15;
        };
        const maxLVR = getMaxLVR(youngestAge);
        const maxLoanAmount = currentHomeValue * (maxLVR / 100);
        // Cap at 75% of age-based capacity — full drawdown rarely optimal vs home strategies and compounds debt heavily.
        [0.25, 0.5, 0.75].forEach((ratio) => {
            const loanAmount = Math.round(maxLoanAmount * ratio);
            if (loanAmount >= 50000) {
                strategies.push({
                    type: 'reverseMortgage',
                    loanAmount,
                    loanPercentage: Math.round((loanAmount / currentHomeValue) * 100),
                    interestRate: REVERSE_MORTGAGE_DEFAULT_RATE,
                    loanStructure: 'lump_sum',
                    establishmentFees: 0,
                    name: `Reverse Mortgage ($${loanAmount.toLocaleString()} lump sum)`,
                    description: `Access ${Math.round(ratio * 100)}% of age-based reverse mortgage capacity as assessable liquid assets`,
                });
            }
        });
    }

    // HEAS: test fortnightly payment steps up to a conservative cap
    if (propertySettings.heas) {
        const clientAge = calculateAge(factFind.client?.dob) || 67;
        const partnerAge = factFind?.adviceFor === 'couple' ? calculateAge(factFind.partner?.dob) : null;
        const referenceAge = partnerAge ? Math.min(clientAge, partnerAge) : clientAge;
        const maxFortnightly = referenceAge >= 80 ? 1500 : referenceAge >= 75 ? 1200 : 1000;
        [250, 500, 750, 1000].filter((amt) => amt <= maxFortnightly).forEach((paymentAmount) => {
            strategies.push({
                type: 'heas',
                paymentAmount,
                paymentFrequency: 'fortnightly',
                interestRate: 3.95,
                name: `HEAS ($${paymentAmount.toLocaleString()}/fortnight)`,
                description: 'Home Equity Access Scheme payments to supplement income (modelled against liquid assets)',
            });
        });
    }

    return strategies;
}

/**
 * Generate debt repayment strategies from client liabilities
 */
export function generateDebtRepaymentStrategies(factFind: DraftFactFind, _problemStatement: ProblemStatement, reserve?: number): StrategyCandidate[] {
    const strategies: StrategyCandidate[] = [];
    const liabilities = decidableClientLiabilities(factFind);
    if (liabilities.length === 0) return strategies;

    const availableAssets = getSahAvailableAssets(factFind, reserve);
    if (availableAssets.length === 0) return strategies;

    liabilities.forEach((liability) => {
        const fullSource = findOptimalAssetSource(availableAssets, liability.balance);
        if (fullSource) {
            strategies.push({
                type: 'debtRepayment',
                liabilityKey: liability.key,
                repaymentAmount: liability.balance,
                liabilityDescription: liability.description,
                interestRate: liability.interestRate,
                fundingSources: fullSource.fundingSources,
                name: `Pay off ${liability.description} ($${liability.balance.toLocaleString()})`,
                description: `Liquidate assessable assets to fully repay ${liability.description} (balance $${liability.balance.toLocaleString()})`,
            });
        }

        const partialAmount = Math.round(liability.balance * 0.5);
        if (partialAmount >= 5000 && partialAmount < liability.balance) {
            const partialSource = findOptimalAssetSource(availableAssets, partialAmount);
            if (partialSource) {
                strategies.push({
                    type: 'debtRepayment',
                    liabilityKey: liability.key,
                    repaymentAmount: partialAmount,
                    liabilityDescription: liability.description,
                    interestRate: liability.interestRate,
                    fundingSources: partialSource.fundingSources,
                    name: `Partial repay ${liability.description} ($${partialAmount.toLocaleString()})`,
                    description: `Repay 50% of ${liability.description} from liquid assets`,
                });
            }
        }
    });

    return strategies;
}

/**
 * Get available liquid assets for strategy funding
 */
function getSahAvailableAssets(factFind: DraftFactFind, reserve?: number): SahAvailableAsset[] {
    return collectLiquidAssetsForSahSuggestions(factFind, reserve ?? getRecommendedEmergencyFund(factFind));
}

/** A buffer the annuity plans leave untouched on top of the emergency money (unchanged since the engine was built). */
const SAH_ANNUITY_CUSHION = 50_000;

/**
 * THE MONEY A GUARANTEED INCOME COULD COME FROM, AS THE PLANS SEE IT — and as the question shows it
 * (GuaranteedIncomeQuestions' "about $X in savings and investments"), so the share the client chooses is
 * a share of the same figure the plans size against: the bank accounts above the emergency money, term
 * deposits, shares and half of each account-based pension, less a further cushion.
 */
export function sahAnnuityInvestable(factFind: DraftFactFind, reserve?: number): { amount: number; madeOf: string } {
    const assets = getSahAvailableAssets(factFind, reserve).filter((a) => a.available > 0);
    const total = assets.reduce((sum, asset) => sum + asset.available, 0);
    const has = (type: string) => assets.some((a) => a.type === type);
    const parts = [
        has('bankAccounts') ? 'bank accounts' : null,
        has('termDeposits') ? 'term deposits' : null,
        has('shares') ? 'shares' : null,
        has('accountBasedPensions') ? 'part of the account-based pension' : null,
    ].filter(Boolean) as string[];
    const madeOf = parts.length <= 1 ? (parts[0] || 'savings') : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
    return { amount: Math.max(0, total - SAH_ANNUITY_CUSHION), madeOf };
}

/**
 * Find optimal asset sources for a given amount (may return multiple assets)
 */
function findOptimalAssetSource(availableAssets: SahAvailableAsset[], amount: number): AssetSourceWithFunding | null {
    const fundingSources: FundingSource[] = [];
    let remainingAmount = amount;

    // Sort by priority (most liquid first)
    const sortedAssets = [...availableAssets].sort((a, b) => a.priority - b.priority);

    // Try to fund from multiple assets. `asset.available` is already net of the recommended emergency reserve
    // (applied upstream in collectLiquidAssetsForSahSuggestions), so no second deduction is needed here.
    for (const asset of sortedAssets) {
        if (remainingAmount <= 0) break;

        const availableFromAsset = asset.available;

        if (availableFromAsset > 0) {
            const amountToTake = Math.min(remainingAmount, availableFromAsset);
            fundingSources.push({
                ...asset,
                amountToTake: amountToTake,
                originalValue: asset.value
            });
            remainingAmount -= amountToTake;
        }
    }

    // If we couldn't fund the full amount, return null (strategy not viable)
    if (remainingAmount > 0) {
        return null;
    }

    // Return the first asset (for backward compatibility) but include all funding sources
    if (fundingSources.length > 0) {
        return {
            ...(fundingSources[0] as unknown as SahAvailableAsset),
            fundingSources: fundingSources, // Include all assets used
            totalAmount: amount
        };
    }

    return null;
}

/**
 * Calculate current funeral bonds in factFind
 */
function calculateCurrentFuneralBonds(factFind: DraftFactFind): number {
    const funeralBonds = factFind.assets?.funeralBonds || [];
    return funeralBonds.reduce((sum, bond) => {
        return sum + (bond.value || 0);
    }, 0);
}

/**
 * Generate all strategies based on enabled settings and problem statement
 */
/**
 * Several levers at once — which is what advice actually looks like.
 *
 * WHAT WAS MISSING. Every candidate above moves ONE thing, and the evaluator applied exactly one
 * strategy type per candidate. So the engine could tell a client to gift $10,000, or to buy a
 * funeral bond, or to buy an annuity, and never that doing two of them together beats either alone.
 * The manual page has always let them stack, so the Suggestion Engine was strictly less capable than
 * the screen beside it — and an adviser stacking these is not an edge case, it is the normal
 * recommendation.
 *
 * WHY THESE COMBINATIONS AND NOT ALL OF THEM. Pairing everything with everything would be hundreds
 * of projections to show what a handful already shows. The ones built here are the ones that
 * compound rather than merely coexist:
 *
 *   - the three ASSET-REDUCTION levers with each other. Each takes a different slice out of what
 *     Centrelink counts — a gift leaves outright, a funeral bond becomes exempt, an annuity is
 *     counted at 60% — and the contribution rates fall with the total, so together they clear
 *     thresholds none of them reaches alone.
 *   - the best DOWNSIZE with the small certain levers, because a downsize is what creates the cash
 *     those levers then need. Sequencing matters here and it is the natural one: the sale lands the
 *     money in the bank, and the gift or the bond is drawn from it.
 *
 * ORDER, AND WHY IT IS THE ORDER IT IS. Each step liquidates from the fact-find the previous step
 * left, so a combination can never fund itself twice — but a later step CAN find its asset already
 * spent and quietly deliver less. So the small, certain, cheap levers run first and the annuity runs
 * last, absorbing whatever remains: the annuity has a $50,000 floor below which it applies nothing
 * at all, which makes it the only one that fails cleanly rather than partially.
 */
export function generateCombinationStrategies(singles: StrategyCandidate[]): StrategyCandidate[] {
    const combos: StrategyCandidate[] = [];

    /** The largest candidate of a type — the one with the most room to move the assessment. */
    const largestOf = (type: string): StrategyCandidate | null => {
        const ofType = singles.filter((s) => s.type === type);
        if (ofType.length === 0) return null;
        const size = (s: StrategyCandidate) => Number(s.purchaseAmount ?? s.amount ?? 0);
        return ofType.reduce((best, s) => (size(s) > size(best) ? s : best));
    };

    const gift = largestOf('gifting');
    const bond = largestOf('funeralBonds');
    const annuity = largestOf('annuity');

    /** Applied in this order for the reason in the doc comment: certain and cheap first. */
    const ORDER = ['gifting', 'funeralBonds', 'annuity', 'downsize'];
    const build = (steps: StrategyCandidate[], name: string, description: string): void => {
        const present = steps.filter(Boolean);
        if (present.length < 2) return;
        const ordered = [...present].sort((a, b) => ORDER.indexOf(a.type) - ORDER.indexOf(b.type));
        combos.push({
            type: 'combination',
            steps: ordered,
            name,
            description,
            groupKey: `combination-${ordered.map((s) => s.type).join('-')}`,
        });
    };

    const money = (s: StrategyCandidate | null) => `$${Number(s?.purchaseAmount ?? s?.amount ?? 0).toLocaleString()}`;

    if (gift && bond) {
        build([gift, bond], `Gift ${money(gift)} and buy ${money(bond)} of funeral bonds`,
            'Two small moves that stack: the gift leaves the assessment outright, the bond becomes exempt. Together they take more out of what Centrelink counts than either does alone.');
    }
    if (gift && annuity) {
        build([gift, annuity], `Gift ${money(gift)} and buy a ${money(annuity)} annuity`,
            'The gift leaves the assessment outright; the annuity is then counted at only 60% of what it costs. The annuity does the heavy lifting and the gift clears the last of the threshold.');
    }
    if (bond && annuity) {
        build([bond, annuity], `Buy ${money(bond)} of funeral bonds and a ${money(annuity)} annuity`,
            'The bond is exempt and the annuity is counted at 60%, so between them a large share of the assessable pool stops being counted in full.');
    }
    if (gift && bond && annuity) {
        build([gift, bond, annuity], `Gift ${money(gift)}, buy ${money(bond)} of funeral bonds and a ${money(annuity)} annuity`,
            'All three asset-reduction levers together. Each takes a different slice out of what Centrelink counts, and the contribution rates fall with the total.');
    }

    /*
     * The downsize is what CREATES the cash the small levers then use, so it is worth pairing even
     * though the downsize family already varies its own options. Only the largest downsize is
     * paired: the others differ in the new home price, which changes how much cash there is, not
     * what a gift or a bond then does with it.
     */
    const downsize = largestOf('downsize');
    if (downsize) {
        if (bond) {
            build([downsize, bond], `${downsize.name}, and buy ${money(bond)} of funeral bonds`,
                'The sale frees up cash that Centrelink then counts in full. Moving part of it into a funeral bond makes that part exempt again, which is the simplest way to soften the downsizing trap.');
        }
        if (gift) {
            build([downsize, gift], `${downsize.name}, and gift ${money(gift)}`,
                'The sale frees up cash that Centrelink then counts in full. Gifting part of it within the yearly free area takes that part out of the assessment straight away.');
        }
    }

    return combos;
}

/** Below this, INTEREST-FREE debts are not tested cleared beside each plan (strategy/debtsToTest.ts). */
export const SAH_DEBT_TWIN_FLOOR = ZERO_INTEREST_DEBT_FLOOR;

/** The steps a candidate is made of (a combination's, or itself). */
const stepsOf = (c: StrategyCandidate): StrategyCandidate[] => (c.type === 'combination' && Array.isArray(c.steps) ? (c.steps as StrategyCandidate[]) : [c]);

/**
 * EVERY PLAN, ALSO WITH THE DEBTS CLEARED (owner, 22 Oct 2026: "every module needs to test paying it off").
 *
 * Paying off a debt was tested only on its own, never beside the plan that led, so a plan like "spend $200,000 on
 * the home" was never tried with the credit card cleared too, and the cards were silent on the debt. As in the
 * village, downsizing and granny flat (their "-clear-debt" twins) and aged care (C.AC.4), each plan now has a twin
 * that does the same and then pays off every debt (not a reverse mortgage), funded from the money the plan itself
 * leaves untouched, cash first and keeping the emergency money. The projection then shows whether clearing the
 * debts does better or worse. A plan that cannot fund the debts as well gets no twin; it is shown as it was.
 */
export function generateDebtClearTwins(candidates: StrategyCandidate[], factFind: DraftFactFind, reserve?: number): StrategyCandidate[] {
    // Which debts: strategy/debtsToTest.ts (never a card paid off monthly; any interest-bearing debt, whatever its
    // size; interest-free debts from SAH_DEBT_TWIN_FLOOR), the rule every module uses (26 Oct 2026).
    const debts = decidableClientLiabilities(factFind);
    if (debts.length === 0) return [];
    const assets = getSahAvailableAssets(factFind, reserve);
    if (assets.length === 0) return [];
    const twins: StrategyCandidate[] = [];
    for (const c of candidates) {
        const steps = stepsOf(c);
        // Debt already in the plan (on its own, in a combination, or repaid from a downsize) is not doubled.
        if (steps.some((s) => s.type === 'debtRepayment' || s.type === 'doNothing' || (s.type === 'downsize' && s.downsizeDetails?.repayDebt))) continue;
        // What this plan leaves of each account.
        const used = new Map<string, number>();
        for (const st of steps) for (const f of (st.fundingSources || []) as FundingSource[]) used.set(`${f.type}-${f.id}`, (used.get(`${f.type}-${f.id}`) || 0) + (Number(f.amountToTake) || 0));
        const left = assets.map((a) => ({ ...a, available: Math.max(0, a.available - (used.get(`${a.type}-${a.id}`) || 0)) }));
        const debtSteps: StrategyCandidate[] = [];
        let ok = true;
        for (const d of debts) {
            const source = findOptimalAssetSource(left, d.balance);
            if (!source) { ok = false; break; }
            for (const f of source.fundingSources) {
                const a = left.find((x) => x.type === f.type && String(x.id) === String(f.id));
                if (a) a.available = Math.max(0, a.available - (Number(f.amountToTake) || 0));
            }
            debtSteps.push({
                type: 'debtRepayment',
                liabilityKey: d.key,
                repaymentAmount: d.balance,
                liabilityDescription: d.description,
                interestRate: d.interestRate,
                fundingSources: source.fundingSources,
                name: `Pay off ${d.description} ($${d.balance.toLocaleString()})`,
                description: `Repay ${d.description} in full from savings the plan leaves untouched`,
            });
        }
        if (!ok || debtSteps.length === 0) continue;
        twins.push({
            type: 'combination',
            // The plan first, as it stands, then the debts from what it leaves.
            steps: [...steps, ...debtSteps],
            name: `${c.name || 'This plan'}, and clear the debts`,
            description: `${c.description || ''} The debts are then paid off from savings the plan leaves untouched.`.trim(),
            groupKey: `${String(c.groupKey ?? c.name ?? c.type)}-clear-debt`,
            clearsDebt: true,
        } as StrategyCandidate);
    }
    return twins;
}

export function generateAllStrategies(factFind: DraftFactFind, problemStatement: ProblemStatement, strategySettings: SupportAtHomeStrategySettingsInput): StrategyCandidate[] {
    const allStrategies: StrategyCandidate[] = [];

    // Generate gifting strategies (if enabled and relevant)
    if (strategySettings.includeGifting) {
        const giftingStrategies = generateGiftingStrategies(factFind, problemStatement, strategySettings.emergencyReserve);
        allStrategies.push(...giftingStrategies);
    }

    // Generate funeral bond strategies (if enabled and relevant)
    if (strategySettings.includeFuneralBonds) {
        const funeralBondStrategies = generateFuneralBondStrategies(factFind, problemStatement, strategySettings.emergencyReserve);
        allStrategies.push(...funeralBondStrategies);
    }

    // Generate annuity strategies (if enabled)
    if (strategySettings.includeAnnuities) {
        const annuityStrategies = generateAnnuityStrategies(factFind, problemStatement, strategySettings.emergencyReserve);
        allStrategies.push(...annuityStrategies);
    }

    // Generate property strategies (if enabled)
    if (strategySettings.includePropertyStrategies) {
        const propertyStrategies = generatePropertyStrategies(
            factFind,
            problemStatement,
            strategySettings.propertyStrategies || {},
            strategySettings.emergencyReserve,
        );
        allStrategies.push(...propertyStrategies);
    }

    // Debt repayment (when liabilities exist; enabled by default)
    if (strategySettings.includeDebtRepayment !== false) {
        const debtStrategies = generateDebtRepaymentStrategies(factFind, problemStatement, strategySettings.emergencyReserve);
        allStrategies.push(...debtStrategies);
    }

    // Whole entity IP sale (include gate only; independent of property-strategy toggles). Cast: the
    // (separately-typed) EntityIpSaleStrategy shape has no index signature, but structurally satisfies
    // StrategyCandidate's known fields (type/name/description/investmentPropertyDetails/...).
    allStrategies.push(...(generateEntityIpSaleStrategies(factFind) as unknown as StrategyCandidate[]));

    // Last, so every single-lever candidate above is available to combine. See
    // generateCombinationStrategies for which combinations are built and why only those.
    allStrategies.push(...generateCombinationStrategies(allStrategies));

    // Every plan also tried with the debts cleared, unless the client switched debt repayment off.
    if (strategySettings.includeDebtRepayment !== false) {
        allStrategies.push(...generateDebtClearTwins(allStrategies, factFind, strategySettings.emergencyReserve));
    }

    return allStrategies;
}
